import { MouseEvent, useEffect, useState, useRef } from 'react';
import { TeacherDetails } from './types';
import { TEACHER_PROFILE_PAGE } from '../cart/donationSource';
import { AnchoredAmounts, MONTHLY_AMOUNTS, REGULAR_AMOUNTS } from '../common/AnchoredAmounts';
import { Svg } from '../common/Svg';

type GiveTab = 'monthly' | 'once';

interface TeacherProfileDonateBarProps {
    teacherDetails: TeacherDetails,
}

const TeacherProfileDonateBar = ({
    teacherDetails,
}: TeacherProfileDonateBarProps) => {
    const [activeTab, setActiveTab] = useState<GiveTab>('monthly');
    const [selectedAmount, setSelectedAmount] = useState<number | null>(MONTHLY_AMOUNTS[2]);
    const [isCustomAmountOpen, setIsCustomAmountOpen] = useState(false);
    const [customAmount, setCustomAmount] = useState('');

    const teacherId = teacherDetails.id;
    const teacherName = teacherDetails.name;
    const possessiveName = teacherDetails.possessiveName;
    const challengeId = teacherDetails.challengeId;
    const hasActiveProjects = teacherDetails.numActiveProjects > 0;
    const referralReason = 'GAVE_THROUGH_TEACHER_PAGE';

    const isMonthly = activeTab === 'monthly';

    useEffect(() => {
        setSelectedAmount(isMonthly ? MONTHLY_AMOUNTS[2] : REGULAR_AMOUNTS[2]);
        setIsCustomAmountOpen(false);
        setCustomAmount('');
    }, [isMonthly]);

    const recurringDonationAction =
        `${secureBaseURL}donors/cart/recurring-donation?recurringDonation=true&gcToTeacher=${teacherId}`;

    const inspireFormAction =
        `${secureBaseURL}donors/cart/teacher-gift-code?gift=true&deliveryType=email` +
        `&gcToTeacher=${teacherId}` +
        `&toName=${encodeURIComponent(teacherName)}` +
        `&message=${encodeURIComponent('Use this toward your next classroom project!')}`;

    const quickDonateAction = `${secureBaseURL}donors/cart/teacher-donation`;

    const formAction = isMonthly
        ? recurringDonationAction
        : (hasActiveProjects ? quickDonateAction : inspireFormAction);

    const effectiveAmount = isCustomAmountOpen
        ? (parseFloat(customAmount) || 0)
        : (selectedAmount || 0);

    const handleCustomAmountToggle = (e: MouseEvent) => {
        e.preventDefault();
        setIsCustomAmountOpen(!isCustomAmountOpen);
    };

    const renderDonationTypeToggle = () => {
        return (
            <fieldset className="donation-type">
                <input
                    id="donation-type-monthly"
                    type="radio"
                    name="donation-type"
                    value="monthly"
                    checked={isMonthly}
                    onChange={() => setActiveTab('monthly')}
                />
                <label htmlFor="donation-type-monthly">
                    <Svg icon="check" attributes={{ role: 'presentation' }} additionalClasses="presentation" /> Monthly
                </label>
                <input
                    id="donation-type-once"
                    type="radio"
                    name="donation-type"
                    value="once"
                    checked={!isMonthly}
                    onChange={() => setActiveTab('once')}
                />
                <label htmlFor="donation-type-once">
                    <Svg icon="check" attributes={{ role: 'presentation' }} additionalClasses="presentation" /> One-time
                </label>
            </fieldset>
        );
    };

    const renderAmountSelection = () => {
        if (isCustomAmountOpen) {
            return (
                <div className='custom'>
                    <div className="custom-give-input-container">
                        <label className="custom-give-input" htmlFor={'custom-amount'}>
                            <span className="screenreader-only">Custom amount in dollars</span>
                            {/* `donationAmount` is used in quickDonateAction */}
                            <input
                                id={'custom-amount'}
                                name="donationAmount"
                                type='number'
                                /* eslint-disable-next-line jsx-a11y/no-autofocus -- Focus custom amount when opening this view */
                                autoFocus
                                inputMode="decimal"
                                pattern="[0-9]*\.?[0-9]{0,2}"
                                min="0"
                                step="0.01"
                                placeholder='100'
                                value={customAmount}
                                onChange={(e) => setCustomAmount(e.target.value)}
                            />
                        </label>
                    </div>
                    <div>
                        <button type="button" className='text-link' onClick={() => setIsCustomAmountOpen(false)}>Cancel</button>
                    </div>
                </div>
            );
        }
        return (
            <div className="suggested-give-amounts">
                {/* `amount` is used in inspireFormAction */}
                <AnchoredAmounts
                    selectedAmount={selectedAmount}
                    setSelectedAmount={setSelectedAmount}
                    monthly={isMonthly}
                    name="donationAmount"
                />
                <div className='custom'>
                    <button type="button" className='text-link custom-amount' onClick={handleCustomAmountToggle} tabIndex={0}>
                        Give another amount
                    </button>
                </div>
            </div>
        );
    };

    const renderDescription = () => {
        if (isMonthly) {
            return (
                <>
                    <p>
                        We&apos;ll charge you once today. Starting next month, you&apos;ll be charged on the 17th of every month. Edit or cancel anytime.
                    </p>
                </>
            );
        } else {
            return (
                <>
                    <p>One-time donation. You won&apos;t be charged again.</p>
                </>
            );
        }
    };

    const giveButtonText = isMonthly ? `Give $${effectiveAmount} monthly` : `Give $${effectiveAmount}`;

    return (
        <div className="give-bar">
            <div className="container">
                <div className="give-bar-banner-headline">
                    <h1 className="thick">
                        Donate to {possessiveName} classroom
                    </h1>
                    <p className="subheader">Every donation goes towards supplies this classroom needs</p>
                </div>
                <div className="give-bar-modal-headline">
                    <h1>
                        Give to {possessiveName} classroom
                    </h1>
                </div>
                <div className="form-redux">
                    <div className="give-type-wrapper">
                        <form action={formAction} method="post" className="form-redux give-form">
                            {renderDonationTypeToggle()}
                            {!isMonthly && <>
                                <input type="hidden" name="amount" value={effectiveAmount} />
                                {hasActiveProjects && <>
                                    <input type="hidden" name="quickteacher" value="true" />
                                    <input type="hidden" name="teacherid" value={teacherId} />
                                    <input type="hidden" name="challengeid" value={challengeId} />
                                    <input type="hidden" name="teacherReferralReason" value={referralReason} />
                                </>}
                            </>}
                            {isMonthly && <>
                                <input type="hidden" name="donationSource" value={TEACHER_PROFILE_PAGE} />
                            </>}
                            {renderAmountSelection()}

                            <div className="inline-button">
                                <button type="submit" className='button-primary' disabled={!effectiveAmount}>
                                    {giveButtonText}
                                </button>
                                {renderDescription()}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TeacherProfileDonateBar;
